import type { REDecisionEntry } from "@/hooks/useREDecisionLog";
import { fmt } from "@/hooks/useREInvestingSim";

interface Recap {
  totalWallet: number;
  totalScore: number;
  readiness: string;
  strongest: REDecisionEntry | null;
  weakest: REDecisionEntry | null;
  tradeoff: string;
  takeaway: string;
  nextAction: string;
}

export default function RecapPanel({
  recap, log,
}: {
  recap: Recap | null;
  log: REDecisionEntry[];
}) {
  if (!recap) return null;
  return (
    <div className="space-y-3 text-left">
      <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3">
        <p className="text-[10px] font-mono uppercase tracking-wider text-primary/80">Readiness label</p>
        <p className="font-heading font-bold text-base text-foreground">{recap.readiness}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-2xl border border-border bg-card/40 px-3 py-2">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Net cash impact</p>
          <p className={`font-heading font-bold ${recap.totalWallet < 0 ? "text-amber-500" : "text-emerald-500"}`}>
            {recap.totalWallet < 0 ? "−" : "+"}{fmt(Math.abs(recap.totalWallet))}
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-card/40 px-3 py-2">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Judgment score</p>
          <p className="font-heading font-bold text-foreground">{recap.totalScore >= 0 ? "+" : ""}{recap.totalScore}</p>
        </div>
      </div>
      {recap.strongest && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
          <p className="text-[10px] font-mono uppercase tracking-wider text-emerald-500/80">Strongest decision</p>
          <p className="text-sm font-semibold text-foreground">{recap.strongest.label}: {recap.strongest.choice}</p>
          <p className="text-xs text-muted-foreground italic">{recap.strongest.lumiSignal}</p>
        </div>
      )}
      {recap.weakest && recap.weakest !== recap.strongest && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 px-3 py-2">
          <p className="text-[10px] font-mono uppercase tracking-wider text-amber-500/80">Weakest decision</p>
          <p className="text-sm font-semibold text-foreground">{recap.weakest.label}: {recap.weakest.choice}</p>
          <p className="text-xs text-muted-foreground italic">{recap.weakest.lumiSignal}</p>
        </div>
      )}
      <div className="rounded-2xl border border-border bg-card/40 px-3 py-2 space-y-1">
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Key tradeoff</p>
        <p className="text-sm text-foreground">{recap.tradeoff}</p>
      </div>
      <div className="rounded-2xl border border-border bg-card/40 px-3 py-2 space-y-1">
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Real-world takeaway</p>
        <p className="text-sm text-foreground">{recap.takeaway}</p>
      </div>
      <div className="rounded-2xl border border-border bg-card/40 px-3 py-2 space-y-1">
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Suggested next mission</p>
        <p className="text-sm text-foreground">{recap.nextAction}</p>
      </div>
      {log.length > 0 && (
        <details className="rounded-2xl border border-border bg-card/40 px-3 py-2">
          <summary className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground cursor-pointer">
            Full decision log ({log.length})
          </summary>
          <ul className="space-y-2 pt-2">
            {log.map((d) => (
              <li key={d.id} className="text-xs border-l-2 border-border pl-2">
                <p className="font-semibold text-foreground">
                  {d.label}{d.round != null ? ` · Q${d.round}` : ""} — {d.choice}
                </p>
                <p className="text-muted-foreground">{d.consequence}</p>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}